import React, { useState, useEffect, useContext } from 'react';
import { Typography, Box, List, ListItem, ListItemText, CircularProgress, Alert, Select, MenuItem, FormControl, Button, Snackbar } from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { updateUser } from '../../services/user';
import { AuthContext } from '../../contexts/AuthContext';

const UserManagement = () => {
  const { isAdmin } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersCollectionRef = collection(db, 'users');
        const data = await getDocs(usersCollectionRef);
        const fetchedUsers = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setUsers(fetchedUsers);
      } catch (err) {
        setError('Nie udało się pobrać listy użytkowników.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUser(userId, { role: newRole });
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
    } catch (err) {
      setError('Błąd podczas aktualizacji roli użytkownika.');
      console.error(err);
    }
  };

  const handleSyncRoles = async () => {
    setSyncing(true);
    try {
      const functions = getFunctions();
      const synchronizeAllUserRoles = httpsCallable(functions, 'synchronizeAllUserRoles');
      const result = await synchronizeAllUserRoles();
      setSnackbar({ open: true, message: result.data.message, severity: 'success' });
    } catch (err) {
      console.error("Błąd synchronizacji ról:", err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Zarządzanie Użytkownikami</Typography>
        {isAdmin && (
          <Button 
            variant="contained" 
            onClick={handleSyncRoles} 
            disabled={syncing}
          >
            {syncing ? <CircularProgress size={24} /> : 'Synchronizuj Role'}
          </Button>
        )}
      </Box>
      <List>
        {users.map(user => (
          <ListItem key={user.id} divider sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <ListItemText 
              primary={user.email}
            />
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={user.role || 'user'}
                onChange={(e) => handleRoleChange(user.id, e.target.value)}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="marketing">Marketing</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </ListItem>
        ))}
      </List>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
